import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";
import "./S04P01.css";
import CodePen from "../../components/Codepen/Codepen";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S08P01({ reveal }: ISlideObject) {
  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 08"
      />
      <DefaultSlide title="Agenda">
        <div>
          <ul>
            <li>SSR vs CSR</li>
            <li>JavaScript Frameworks.</li>
            <li>Exposición de proyectos.</li>
            <li>Defensa de proyecto.</li>
          </ul>
        </div>
      </DefaultSlide>
      <section>
        <TitleSlide title="SSR vs CSR" subtitle="¿Qué es cada uno?" />
        <DefaultSlide title="SSR (Server-Side Rendering)">
          <div>
            <ul>
              <li>El servidor genera el HTML y lo envía al cliente.</li>
              <li>Mejor para SEO y rendimiento inicial.</li>
              <li>Menor interacción del usuario.</li>
              <li>Ejemplos: Next.js, Nuxt.js.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="CSR (Client-Side Rendering)">
          <div>
            <ul>
              <li>
                La aplicación se carga una sola vez y se actualiza
                dinámicamente.
              </li>
              <li>Mejor experiencia de usuario y velocidad de navegación.</li>
              <li>Requiere más recursos del cliente.</li>
              <li>Ejemplos: React, Vue.js, Angular.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Comparación SSR vs CSR">
          <div style={{ fontSize: "1.5em" }}>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Aspecto</th>
                  <th>SSR</th>
                  <th>CSR</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Rendimiento inicial</td>
                  <td>Rápido</td>
                  <td>Más lento</td>
                </tr>
                <tr>
                  <td>SEO</td>
                  <td>Mejor</td>
                  <td>Limitado</td>
                </tr>
                <tr>
                  <td>Interacción del usuario</td>
                  <td>Menor</td>
                  <td>Mayor</td>
                </tr>
                <tr>
                  <td>Requisitos del cliente</td>
                  <td>Menores</td>
                  <td>Mayores</td>
                </tr>
                <tr>
                  <td>Ejemplos</td>
                  <td>Next.js, Nuxt.js</td>
                  <td>React, Vue.js, Angular</td>
                </tr>
              </tbody>
            </table>
          </div>
        </DefaultSlide>
        <DefaultSlide title="¿Por qué las CSR son más comunes?">
          <div style={{ fontSize: "0.85em" }}>
            <ul>
              <li>Ofrecen una experiencia de usuario más fluida y rápida.</li>
              <li>Permiten una navegación sin recargas completas de página.</li>
              <li>Son ideales para aplicaciones modernas y dinámicas.</li>
              <li>Facilitan la integración con APIs y servicios externos.</li>
              <li>
                Proporcionan una mayor flexibilidad en el diseño de la interfaz.
              </li>
              <li>
                La mayoría de los frameworks modernos están optimizados para
                SPA.
              </li>
            </ul>
          </div>
        </DefaultSlide>
        <section>
          <img src="/images/S08/ssr-vs-csr.png" alt="SSR vs CSR" />
        </section>
        <section>
          <p>
            Veamos un ejemplo de una aplicación CSR sin el uso de frameworks
          </p>
        </section>
        <section>
          <CodePen title="Ejemplo de CSR" penId="zxYVajL" />
        </section>
        <section>
          <p>Cómo se gestiona en VueJS?</p>
          <a href="https://vuejs.org/guide/scaling-up/routing.html" target="_blank" rel="noopener noreferrer">https://vuejs.org/guide/scaling-up/routing.html</a>
        </section>
      </section>
      <section>
        <TitleSlide
          title="JavaScript Frameworks"
          subtitle="Por qué son necesarios?"
        />
        <DefaultSlide title="JavaScript Frameworks">
          <div>
            <ul>
              <li>Permiten la creación de aplicaciones más complejas.</li>
              <li>
                Facilitan la gestión del estado y la interacción con el DOM.
              </li>
              <li>
                Proporcionan herramientas para el enrutamiento y la gestión de
                datos.
              </li>
              <li>Mejoran la experiencia del desarrollador.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="JavaScript Frameworks">
          <div>
            <ul>
              <li>Facilitan la colaboración en equipo.</li>
              <li>Proporcionan una comunidad activa y soporte.</li>
              <li>
                Permiten la creación de aplicaciones más escalables y
                mantenibles.
              </li>
              <li>
                Facilitan la integración con otras tecnologías y herramientas.
              </li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="State Management">
          <div style={{ fontSize: "0.85em" }}>
            <ul>
              <li>Es el proceso de manejar el estado de una aplicación.</li>
              <li>
                Incluye datos como el estado de la interfaz de usuario y datos
                del usuario.
              </li>
              <li>Facilita la sincronización entre la vista y los datos.</li>
              <li>Ayuda a mantener la lógica de la aplicación organizada.</li>
              <li>
                Existen herramientas como Redux, Vuex, y Context API para
                gestionarlo.
              </li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Routing Management">
          <div style={{ fontSize: "0.85em" }}>
            <ul>
              <li>Permite la navegación entre diferentes vistas o páginas.</li>
              <li>
                Facilita la creación de aplicaciones de una sola página (SPA).
              </li>
              <li>
                Proporciona herramientas para manejar rutas dinámicas y
                parámetros.
              </li>
              <li>
                Ayuda a mantener la interfaz de usuario sincronizada con la URL.
              </li>
              <li>
                Ejemplos de herramientas: React Router, Vue Router, Angular
                Router.
              </li>
            </ul>
          </div>
        </DefaultSlide>
        <section>
          Veamos un ejemplo rápido utilizando VueJS.
        </section>
      </section>
      <TitleSlide title="Exposición de proyectos" />
      <TitleSlide title="Defensa
       de proyectos" />
      
      <ImageSlide image="/images/S06/dancing6.gif" title="Fin de parte 1" />
    </>
  );
}
